import type {
  AnalysisJsonObject,
  AnalysisJsonValue,
  AnalysisRecipeState,
  AnalysisRecipeStep,
} from '@/types/analysisRecipe.types';
import { analysisChartReferencesAny } from '@/helpers/analysisChartReferences.helpers';

/**
 * Detects whether a JSON config references any of the given column fields via
 * known shapes (column operands, leaf/control `field`, chart axes, window
 * sources). Bare string literals are ignored to avoid false positives on
 * relationship names, operators, and labels.
 */
const jsonReferencesAny = (
  value: AnalysisJsonValue,
  fields: Set<string>,
): boolean => {
  if (Array.isArray(value)) {
    return value.some((entry) => jsonReferencesAny(entry, fields));
  }
  if (!value || typeof value !== 'object') return false;

  const object = value as AnalysisJsonObject;
  if (
    object.kind === 'column' &&
    typeof object.value === 'string' &&
    fields.has(object.value)
  ) {
    return true;
  }
  if (typeof object.field === 'string' && fields.has(object.field)) {
    return true;
  }
  if (typeof object.xAxis === 'string' && fields.has(object.xAxis)) {
    return true;
  }
  if (
    Array.isArray(object.yAxisColumns) &&
    object.yAxisColumns.some(
      (column) => typeof column === 'string' && fields.has(column),
    )
  ) {
    return true;
  }
  if (
    typeof object.sourceColumn === 'string' &&
    fields.has(object.sourceColumn)
  ) {
    return true;
  }
  if (
    typeof object.groupByColumn === 'string' &&
    fields.has(object.groupByColumn)
  ) {
    return true;
  }
  if (
    typeof object.column === 'string' &&
    (object.kind === 'column' || object.type === 'column') &&
    fields.has(object.column)
  ) {
    return true;
  }

  return Object.values(object).some((entry) =>
    jsonReferencesAny(entry, fields),
  );
};

const breakdownReferencesAny = (
  breakdown: NonNullable<
    AnalysisRecipeState['sheets'][string]['breakdown']
  >,
  fields: Set<string>,
): boolean =>
  // A total sheet has no group column, so it cannot be broken by one going away.
  (breakdown.groupByColumn !== undefined &&
    fields.has(breakdown.groupByColumn)) ||
  (breakdown.aggregations ?? []).some(
    (aggregation) =>
      Boolean(aggregation.field && fields.has(aggregation.field)),
  ) ||
  (breakdown.windows ?? []).some((window) =>
    fields.has(window.sourceColumn),
  );

/**
 * Confirmation copy for removing an analysis column. Every consequence the
 * cascade will apply must be named here: charts referencing the column are
 * deleted outright, so omitting them meant a user could lose a configured chart
 * with no warning.
 */
export function analysisRemovalMessage(
  impact: AnalysisRemovalImpact | null | undefined,
): string {
  const dependents = Math.max(0, (impact?.stepIds.length ?? 1) - 1);
  const parts = [`Remove this column and ${dependents} dependent step(s)?`];
  if (impact?.clearsKeepFilter) {
    parts.push('The Keep filter also depends on it and will be cleared.');
  }
  if (impact?.clearedControlIds.length) {
    parts.push(
      `${impact.clearedControlIds.length} promoted control(s) will be cleared.`,
    );
  }
  if (impact?.clearedChartIds.length) {
    parts.push(
      `${impact.clearedChartIds.length} chart(s) built on it will be removed.`,
    );
  }
  if (impact?.brokenChildSheetIds.length) {
    parts.push(
      `${impact.brokenChildSheetIds.length} child sheet(s) will be marked broken, not deleted.`,
    );
  }
  return parts.join(' ');
}

export interface AnalysisCalcOrder {
  /** Calculations in an order where every operand column already exists. */
  ordered: AnalysisRecipeStep[];
  /**
   * Calculations that can never be placed: they read a calculation column that
   * is only produced later, or their own output. Authoring prevents this, so a
   * non-empty list means a hand-edited or imported recipe.
   */
  unresolved: AnalysisRecipeStep[];
}

/**
 * Orders calculations so each one's `derive_rows` stage runs after the stages
 * producing the columns it reads. The backend binds every derivation in a
 * single `WITH` against the pre-stage columns, so chaining only works across
 * stages — declaration order alone is not enough once a step is edited to point
 * at a calculation that was authored after it.
 */
export function orderAnalysisCalcSteps(
  calcSteps: AnalysisRecipeStep[],
): AnalysisCalcOrder {
  const remaining = [...calcSteps];
  // Every calc column starts pending; a step referencing one waits for it.
  // A self-reference never clears, which is exactly the unresolved case.
  const pending = new Set(remaining.flatMap((step) => step.outputColumns));
  const ordered: AnalysisRecipeStep[] = [];
  let progressed = true;
  while (remaining.length > 0 && progressed) {
    progressed = false;
    for (let index = 0; index < remaining.length; index += 1) {
      const step = remaining[index];
      if (jsonReferencesAny(step.config, pending)) continue;
      ordered.push(step);
      step.outputColumns.forEach((column) => pending.delete(column));
      remaining.splice(index, 1);
      progressed = true;
      break;
    }
  }
  return { ordered, unresolved: remaining };
}

export interface AnalysisRemovalImpact {
  stepIds: string[];
  outputColumns: string[];
  clearsKeepFilter: boolean;
  clearedControlIds: string[];
  clearedChartIds: string[];
  brokenChildSheetIds: string[];
}

/**
 * Computes the same-sheet transitive dependency cascade for removing a Made
 * step, plus child sheets whose grouping source would break. Child sheets are
 * reported but never deleted silently. Charts and controls that reference
 * removed columns are listed for the store to clear.
 */
export function buildAnalysisRemovalImpact(
  state: AnalysisRecipeState,
  sheetId: string,
  stepId: string,
): AnalysisRemovalImpact {
  const sheet = state.sheets[sheetId];
  const target = sheet?.steps.find((step) => step.id === stepId);
  if (!sheet || !target) {
    return {
      stepIds: [],
      outputColumns: [],
      clearsKeepFilter: false,
      clearedControlIds: [],
      clearedChartIds: [],
      brokenChildSheetIds: [],
    };
  }

  const stepIds = new Set([target.id]);
  const outputColumns = new Set(target.outputColumns);
  let changed = true;
  while (changed) {
    changed = false;
    sheet.steps.forEach((step) => {
      if (
        !stepIds.has(step.id) &&
        jsonReferencesAny(step.config, outputColumns)
      ) {
        stepIds.add(step.id);
        step.outputColumns.forEach((column) => outputColumns.add(column));
        changed = true;
      }
    });
  }

  return {
    stepIds: [...stepIds],
    outputColumns: [...outputColumns],
    clearsKeepFilter: Boolean(
      sheet.keepFilter &&
        jsonReferencesAny(sheet.keepFilter, outputColumns),
    ),
    clearedControlIds: sheet.controls
      .filter((control) => outputColumns.has(control.field))
      .map((control) => control.id),
    clearedChartIds: sheet.charts
      .filter((chart) =>
        analysisChartReferencesAny(chart.config, outputColumns),
      )
      .map((chart) => chart.id),
    brokenChildSheetIds: Object.values(state.sheets)
      .filter(
        (candidate) =>
          candidate.breakdown?.parentSheetId === sheet.id &&
          breakdownReferencesAny(candidate.breakdown, outputColumns),
      )
      .map((candidate) => candidate.id),
  };
}
